import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import {
    Plus,
    Upload,
    User,
    Briefcase,
    AlertCircle,
    Shield,
    Eye,
    CheckDouble,
    Info,
    Calendar,
    Hammer,
    Wrench,
    ShieldCheck,
    Camera,
    XCircle,
    Loader2
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';
import { compressImage } from '../../utils/upload';

interface AddFieldWorkerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    worker?: any;
}

const AddFieldWorkerModal: React.FC<AddFieldWorkerModalProps> = ({ isOpen, onClose, onRefresh, worker }) => {
    const [formData, setFormData] = useState({
        name: '',
        employeeId: '',
        email: '',
        phone: '',
        primaryLocationId: '',
        department: '',
        jobTitle: '',
        startDate: '',
        isActive: true,
        emergencyContactName: '',
        emergencyContactPhone: '',
        requiresApprovalForIssuances: false,
        canViewAllLocations: false,
        additionalNotes: '',
        permissionLevel: 'Field Worker',
        profilePhoto: ''
    });

    const [locations, setLocations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string>(worker?.profilePhoto || '');
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        return () => {
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
        };
    }, [photoPreview]);

    useEffect(() => {
        if (isOpen) {
            fetchMeta();
            if (worker) {
                setFormData({
                    name: worker.name || '',
                    employeeId: worker.employeeId || '',
                    email: worker.email || '',
                    phone: worker.phone || '',
                    primaryLocationId: worker.primaryLocation?.id?.toString() || '',
                    department: worker.department || '',
                    jobTitle: worker.jobTitle || '',
                    startDate: worker.startDate ? new Date(worker.startDate).toISOString().split('T')[0] : '',
                    isActive: worker.isActive !== undefined ? worker.isActive : true,
                    emergencyContactName: worker.emergencyContactName || '',
                    emergencyContactPhone: worker.emergencyContactPhone || '',
                    requiresApprovalForIssuances: worker.requiresApprovalForIssuances || false,
                    canViewAllLocations: worker.canViewAllLocations || false,
                    additionalNotes: worker.additionalNotes || '',
                    permissionLevel: worker.permissionLevel || 'Field Worker',
                    profilePhoto: worker.profilePhoto || ''
                });
                setPhotoPreview(worker.profilePhoto || '');
            } else {
                setFormData({
                    name: '',
                    employeeId: '',
                    email: '',
                    phone: '',
                    primaryLocationId: '',
                    department: '',
                    jobTitle: '',
                    startDate: new Date().toISOString().split('T')[0],
                    isActive: true,
                    emergencyContactName: '',
                    emergencyContactPhone: '',
                    requiresApprovalForIssuances: false,
                    canViewAllLocations: false,
                    additionalNotes: '',
                    permissionLevel: 'Field Worker',
                    profilePhoto: ''
                });
                setPhotoPreview('');
            }
        }
    }, [isOpen, worker]);

    const fetchMeta = async () => {
        try {
            const locs = await api.get('/api/inventory/locations');
            setLocations(locs);
        } catch (err) {
            console.error(err);
        }
    };

    const handleQuickSetup = (type: 'construction' | 'maintenance' | 'safety') => {
        if (type === 'construction') {
            setFormData(prev => ({
                ...prev,
                department: 'Construction',
                jobTitle: 'Site Worker',
                permissionLevel: 'Construction Worker'
            }));
        } else if (type === 'maintenance') {
            setFormData(prev => ({
                ...prev,
                department: 'Maintenance',
                jobTitle: 'Maintenance Technician',
                canViewAllLocations: true,
                permissionLevel: 'Maintenance Tech'
            }));
        } else if (type === 'safety') {
            setFormData(prev => ({
                ...prev,
                department: 'Safety',
                jobTitle: 'Safety Officer',
                canViewAllLocations: true,
                requiresApprovalForIssuances: true,
                permissionLevel: 'Safety Officer'
            }));
        }
        toast.info(`Applied ${type.replace(/^\w/, c => c.toUpperCase())} quick setup`);
    };

    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validation
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPEG, PNG, GIF and WebP images are allowed.');
            return;
        }

        try {
            setLoading(true);
            // Compress and resize the image
            const compressedFile = await compressImage(file, 1024, 1024, 0.7);

            if (compressedFile.size > 5 * 1024 * 1024) {
                toast.error('File size still exceeds 5MB limit.');
                return;
            }

            setSelectedFile(compressedFile);
            const previewUrl = URL.createObjectURL(compressedFile);
            setPhotoPreview(previewUrl);
        } catch (error) {
            console.error('Error processing image:', error);
            toast.error('Failed to process image');
        } finally {
            setLoading(false);
        }
    };

    const handleRemovePhoto = () => {
        setSelectedFile(null);
        setPhotoPreview('');
        setFormData(prev => ({ ...prev, profilePhoto: '' }));
    };

    const handleSubmit = async (status: 'Active' | 'Draft') => {
        if (!formData.name || !formData.employeeId || !formData.email || !formData.primaryLocationId) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);

            let finalPhotoUrl = formData.profilePhoto;

            // If a new file was selected, upload it first
            if (selectedFile) {
                const formDataUpload = new FormData();
                formDataUpload.append('photo', selectedFile);
                formDataUpload.append('workerName', formData.name || 'field-worker');

                const uploadResponse = await api.post('/api/inventory/workers/upload', formDataUpload);
                if (uploadResponse.success) {
                    finalPhotoUrl = uploadResponse.url;
                } else {
                    throw new Error(uploadResponse.message || 'Failed to upload photo');
                }
            }

            const payload = { ...formData, profilePhoto: finalPhotoUrl, status };
            if (worker) {
                await api.patch(`/api/inventory/workers/${worker.id}`, payload);
                toast.success('Worker updated successfully');
            } else {
                await api.post('/api/inventory/workers', payload);
                toast.success('Worker added successfully');
            }
            onRefresh();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save worker');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-xl max-h-[90vh] overflow-hidden rounded-[28px] sm:rounded-3xl p-0 border-none shadow-2xl flex flex-col">
                <div className="p-5 sm:p-6 border-b border-gray-50 flex-shrink-0">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 text-lg sm:text-xl font-black tracking-tight">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-50 flex items-center justify-center shadow-sm">
                                <Plus className="text-blue-600" size={18} />
                            </div>
                            {worker ? 'Edit Field Worker' : 'New Field Worker'}
                        </DialogTitle>
                    </DialogHeader>
                </div>

                <div className="p-5 sm:p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar">

                    {/* Profile Photo Area */}
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="relative group">
                            <div className="w-24 h-24 bg-gray-50 border-2 border-dashed border-gray-200 rounded-full flex flex-col items-center justify-center text-gray-400 space-y-1 overflow-hidden transition-all group-hover:border-blue-400 group-hover:bg-blue-50/30">
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Profile" className="w-full h-full object-cover" />
                                ) : (
                                    <>
                                        <User size={32} className="opacity-20" />
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-center">Profile<br />Photo</span>
                                    </>
                                )}
                            </div>
                            {photoPreview && (
                                <button
                                    onClick={handleRemovePhoto}
                                    className="absolute -top-1 -right-1 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center shadow-lg hover:bg-red-200 transition-colors border-2 border-white"
                                >
                                    <XCircle size={14} />
                                </button>
                            )}
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handlePhotoChange}
                            accept="image/*"
                            className="hidden"
                        />
                        <Button
                            variant="outline"
                            className="rounded-xl flex items-center gap-2 text-xs font-bold shadow-sm h-9 hover:bg-gray-50 active:scale-95 transition-all"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={loading}
                        >
                            {loading && selectedFile ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Camera size={14} />
                                    {photoPreview ? 'Change Photo' : 'Upload Photo'}
                                </>
                            )}
                        </Button>
                        {!photoPreview && (
                            <p className="text-[10px] text-gray-400 font-medium text-center leading-relaxed max-w-[200px]">
                                Accepts JPG, PNG, GIF, or WebP. Max size: 5MB.
                            </p>
                        )}
                    </div>

                    <div className="space-y-6 sm:space-y-8">
                        {/* Section 1: Personal Information */}
                        <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-center gap-2 text-xs sm:text-sm font-black text-gray-800 uppercase tracking-wider">
                                <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center">
                                    <User size={14} className="text-gray-400" />
                                </div>
                                Personal Info
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Full Name *</label>
                                    <Input
                                        placeholder="e.g., John Smith" className="rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Employee ID *</label>
                                    <Input
                                        placeholder="e.g., EMP001" className="rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.employeeId} onChange={e => setFormData({ ...formData, employeeId: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Email Address *</label>
                                    <Input
                                        placeholder="john.smith@company.com" className="rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    />
                                    <p className="text-[10px] text-gray-400 font-medium ml-1">
                                        Fieldworker will receive an email with the Issuance details
                                    </p>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Phone Number</label>
                                    <Input
                                        placeholder="+1 (555) 123-4567" className="rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Work Information */}
                        <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-center gap-2 text-xs sm:text-sm font-black text-gray-800 uppercase tracking-wider">
                                <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center">
                                    <Briefcase size={14} className="text-gray-400" />
                                </div>
                                Work Details
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                                <div className="space-y-1.5 col-span-1">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Location *</label>
                                    <Select value={formData.primaryLocationId} onValueChange={val => setFormData({ ...formData, primaryLocationId: val })}>
                                        <SelectTrigger className="rounded-xl border-gray-100 bg-gray-50/50">
                                            <SelectValue placeholder="Select location" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {locations.map(l => <SelectItem key={l.id} value={l.id.toString()}>{l.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5 col-span-1">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Department</label>
                                    <Input
                                        placeholder="e.g., Construction, Maintenance" className="rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.department} onChange={e => setFormData({ ...formData, department: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5 col-span-1">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Job Title</label>
                                    <Input
                                        placeholder="e.g., Site Worker" className="rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.jobTitle} onChange={e => setFormData({ ...formData, jobTitle: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Start Date</label>
                                    <div className="relative">
                                        <Input
                                            type="date" className="rounded-xl border-gray-100 bg-gray-50/50 pl-10"
                                            value={formData.startDate} onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                                        />
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    </div>
                                </div>
                                <div className="flex flex-col justify-end pb-1.5">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                                        className={cn(
                                            "flex items-center gap-3 p-3 rounded-2xl border transition-all text-left h-11",
                                            formData.isActive ? "bg-blue-50/50 border-blue-100 text-blue-700" : "bg-gray-50/50 border-gray-100 text-gray-400 opacity-60"
                                        )}
                                    >
                                        <div className={cn(
                                            "w-5 h-5 rounded-md flex items-center justify-center border transition-all",
                                            formData.isActive ? "bg-blue-600 border-blue-600" : "bg-white border-gray-200"
                                        )}>
                                            {formData.isActive && <div className="w-2 h-2 bg-white rounded-full" />}
                                        </div>
                                        <span className="text-xs font-bold">Worker is Active</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Section 3: Emergency Contact */}
                        <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-center gap-2 text-xs sm:text-sm font-black text-gray-800 uppercase tracking-wider">
                                <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center">
                                    <AlertCircle size={14} className="text-gray-400" />
                                </div>
                                Emergency Contact
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Contact Name</label>
                                    <Input
                                        placeholder="e.g., Jane Smith (Spouse)" className="rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.emergencyContactName} onChange={e => setFormData({ ...formData, emergencyContactName: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-wider">Contact Phone</label>
                                    <Input
                                        placeholder="+1 (555) 987-6543" className="rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.emergencyContactPhone} onChange={e => setFormData({ ...formData, emergencyContactPhone: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 4: Permissions & Access */}
                        <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-center gap-2 text-xs sm:text-sm font-black text-gray-800 uppercase tracking-wider">
                                <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center">
                                    <Shield size={14} className="text-gray-400" />
                                </div>
                                Permissions
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, requiresApprovalForIssuances: !formData.requiresApprovalForIssuances })}
                                    className={cn(
                                        "flex flex-col p-3 sm:p-4 rounded-2xl border transition-all text-left space-y-2",
                                        formData.requiresApprovalForIssuances ? "bg-orange-50/50 border-orange-100" : "bg-gray-50/50 border-gray-100"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "w-5 h-5 rounded-md flex items-center justify-center border transition-all",
                                            formData.requiresApprovalForIssuances ? "bg-orange-600 border-orange-600" : "bg-white border-gray-200"
                                        )}>
                                            {formData.requiresApprovalForIssuances && <div className="w-2 h-2 bg-white rounded-full" />}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <AlertCircle size={14} className={formData.requiresApprovalForIssuances ? "text-orange-600" : "text-gray-400"} />
                                            <span className={cn("text-xs font-bold", formData.requiresApprovalForIssuances ? "text-orange-900" : "text-gray-500")}>Requires Approval for Issuances</span>
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-gray-400 font-medium pl-8">All equipment requests require manager approval</p>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, canViewAllLocations: !formData.canViewAllLocations })}
                                    className={cn(
                                        "flex flex-col p-3 sm:p-4 rounded-2xl border transition-all text-left space-y-2",
                                        formData.canViewAllLocations ? "bg-purple-50/50 border-purple-100" : "bg-gray-50/50 border-gray-100"
                                    )}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "w-5 h-5 rounded-md flex items-center justify-center border transition-all",
                                            formData.canViewAllLocations ? "bg-purple-600 border-purple-600" : "bg-white border-gray-200"
                                        )}>
                                            {formData.canViewAllLocations && <div className="w-2 h-2 bg-white rounded-full" />}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Eye size={14} className={formData.canViewAllLocations ? "text-purple-600" : "text-gray-400"} />
                                            <span className={cn("text-xs font-bold", formData.canViewAllLocations ? "text-purple-900" : "text-gray-500")}>Can View All Locations</span>
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-gray-400 font-medium pl-8">Access to inventory across multiple locations</p>
                                </button>
                            </div>

                            <div className="p-3 sm:p-4 bg-gray-50/80 rounded-2xl border border-gray-100 space-y-3">
                                <div className="flex items-center gap-2">
                                    <Eye size={16} className="text-blue-500" />
                                    <span className="text-xs sm:text-sm font-bold text-gray-800 truncate">Level: {formData.permissionLevel}</span>
                                </div>
                                <ul className="space-y-1.5">
                                    {['View assigned location inventory', 'Request equipment issuance', 'View personal issuance history', 'Update personal profile information'].map((item, i) => (
                                        <li key={i} className="flex items-center gap-2 text-[11px] text-gray-500 font-medium">
                                            <div className="w-1 h-1 rounded-full bg-gray-300" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Section 5: Additional Notes */}
                        <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-center gap-2 text-xs sm:text-sm font-black text-gray-800 uppercase tracking-wider">
                                Notes
                            </div>
                            <textarea
                                className="w-full h-24 rounded-2xl border-gray-100 bg-gray-50/50 p-4 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all font-medium resize-none placeholder:text-gray-300"
                                placeholder="Special requirements, certifications, notes about the worker..."
                                value={formData.additionalNotes} onChange={e => setFormData({ ...formData, additionalNotes: e.target.value })}
                            />
                        </div>

                        {/* Section 6: Quick Setup */}
                        <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-center gap-2 text-xs sm:text-sm font-black text-gray-800 uppercase tracking-wider">
                                <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                                    <Info size={14} className="text-blue-500" />
                                </div>
                                Quick Setup
                            </div>
                            <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                <button
                                    type="button"
                                    onClick={() => handleQuickSetup('construction')}
                                    className="p-3 sm:p-4 bg-white border border-gray-100 rounded-2xl hover:border-orange-200 hover:shadow-md transition-all text-left space-y-2 group"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                                        <Hammer size={16} className="text-orange-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-xs font-bold text-gray-900 leading-tight">Construction Worker</p>
                                        <p className="text-[9px] text-gray-400 font-medium leading-relaxed">Standard site worker with basic access</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleQuickSetup('maintenance')}
                                    className="p-3 sm:p-4 bg-white border border-gray-100 rounded-2xl hover:border-blue-200 hover:shadow-md transition-all text-left space-y-2 group"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                                        <Wrench size={16} className="text-blue-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-xs font-bold text-gray-900 leading-tight">Maintenance Tech</p>
                                        <p className="text-[9px] text-gray-400 font-medium leading-relaxed">Multi-location access for maintenance</p>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleQuickSetup('safety')}
                                    className="p-3 sm:p-4 bg-white border border-gray-100 rounded-2xl hover:border-green-200 hover:shadow-md transition-all text-left space-y-2 group"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
                                        <ShieldCheck size={16} className="text-green-500" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-xs font-bold text-gray-900 leading-tight">Safety Officer</p>
                                        <p className="text-[9px] text-gray-400 font-medium leading-relaxed">Enhanced access with approval workflow</p>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-5 sm:p-6 bg-white border-t border-gray-50 flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
                    <Button variant="ghost" className="order-3 sm:order-1 sm:flex-1 rounded-2xl h-12 sm:h-14 font-black text-[11px] uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-all" onClick={onClose}>Discard</Button>
                    <Button variant="outline" className="order-2 sm:flex-1 rounded-2xl h-12 sm:h-14 font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 border-gray-100"
                        onClick={() => handleSubmit('Draft')} disabled={loading}>
                        <Briefcase size={16} />
                        Save Draft
                    </Button>
                    <Button className="order-1 sm:order-3 sm:flex-[1.5] rounded-2xl h-12 sm:h-14 bg-slate-900 hover:bg-slate-800 text-white font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-2xl shadow-slate-900/20 active:scale-[0.98] transition-all"
                        onClick={() => handleSubmit('Active')} disabled={loading}>
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                <Plus size={18} />
                                {worker ? 'Update Worker' : 'Create Worker'}
                            </>
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddFieldWorkerModal;
